/**
 * Comparison form of portal-internal addresses (`pathname + search`).
 *
 * A separate file because two modules need the same folding and neither may
 * import the other without closing a cycle: `useGuideNavigate.ts` uses it to
 * bind its self-navigation marker to its target, `chatHandoff.ts` to bind the
 * handoff to the address where it may be redeemed — and `useGuideNavigate`
 * already imports `chatHandoff`.
 *
 * Two separate foldings would be worse than the cycle: the marker and the
 * handoff decide about the *same* navigation, and the weaker of the two would
 * be the one that counts.
 */

/** A trailing slash does not matter for the equality check ('/settings/' == '/settings'). */
export function normalizePathname(pathname: string): string {
    return pathname.length > 1 && pathname.endsWith('/') ? pathname.slice(0, -1) : pathname;
}

/**
 * Path for comparisons: without trailing slash and lowercased, because
 * react-router 6 matches with `caseSensitive: false` by default — `/Entity`
 * lands on the same route as `/entity` and must see the same guards.
 */
export function foldPathname(pathname: string): string {
    return normalizePathname(pathname).toLowerCase();
}

/**
 * Query for comparisons: sorted by name and uniformly encoded, so that
 * `?tab=a&panel=b` and `?panel=b&tab=a` count as the same parameters (contract:
 * "same route + same parameters → do not navigate"). Additional parameters
 * deliberately remain a difference — that is a different view.
 */
export function foldSearch(search: string): string {
    const params = new URLSearchParams(search);
    params.sort();
    const canonical = params.toString();
    return canonical ? `?${canonical}` : '';
}

/** Comparison form of an address `pathname + search`; a hash is left out. */
export function foldAddress(address: string): string {
    const hashStart = address.indexOf('#');
    const withoutHash = hashStart >= 0 ? address.slice(0, hashStart) : address;
    const queryStart = withoutHash.indexOf('?');
    const pathname = queryStart >= 0 ? withoutHash.slice(0, queryStart) : withoutHash;
    const search = queryStart >= 0 ? withoutHash.slice(queryStart) : '';
    return `${foldPathname(pathname)}${foldSearch(search)}`;
}
